import {
    Component,
    EventEmitter,
    Input,
    Output,
    SimpleChanges
} from '@angular/core';
import {
    ApproversMissionApprovalDto,
    DisplayableMissionDto,
    findActiveApprovals,
    FlyFreelyError,
    FlyFreelyLoggingService,
    MissionMessageDto,
    MissionService,
    PersonsOrganisationDto,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * The mission's message thread, with a message box for those who can send one.
 * The heading, and its refresh button, belong to the screen showing it.
 */
@Component({
    selector: 'mission-comms',
    templateUrl: './mission-comms.component.html',
    styleUrls: ['./mission-comms.component.scss']
})
export class MissionComms {
    @Input() mission: DisplayableMissionDto;
    @Input() organisation: PersonsOrganisationDto;
    @Input() approval?: ApproversMissionApprovalDto;
    /**
     * The screen showing the messages is working.
     */
    @Input() working: boolean;
    /**
     * Emitted asynchronously, as the messages start loading while the screen
     * showing them is being checked, and that screen's heading shows the
     * loading state.
     */
    @Output() loadingChange = new EventEmitter<boolean>(true);

    discussions: MissionMessageDto[];
    message: string;

    /**
     * Messages can be read on every mission, but can't be added once it's
     * finalised, or by people who can't edit or approve it.
     */
    canSendMessage = false;
    readOnlyMessage: string = null;

    /**
     * A message is being sent, or the screen showing the messages is working,
     * so another can't be sent yet.
     */
    isWorking = false;

    /**
     * Tracks loading the messages, which the screen shows in its heading.
     */
    private loadingTracker = new WorkTracker();
    /**
     * Tracks sending a message, with the screen's work as its parent.
     */
    private workTracker = new WorkTracker();
    private parentWorking$ = new BehaviorSubject(false);
    private ngUnsubscribe$ = new Subject<void>();
    constructor(
        private missionService: MissionService,
        private logging: FlyFreelyLoggingService
    ) {
        this.loadingTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(loading => this.loadingChange.emit(loading));
        this.workTracker.setParentWorkingObservable(this.parentWorking$);
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.isWorking = working));
    }

    ngOnInit() {
        this.missionService.commsChanges$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.refreshDiscussions());
    }

    ngOnChanges(changes: SimpleChanges) {
        if ('working' in changes) {
            this.parentWorking$.next(this.working === true);
        }
        if (this.mission == null) {
            this.discussions = null;
            this.canSendMessage = false;
            this.readOnlyMessage = null;
            return;
        }
        this.refreshCanSendMessage();
        if ('mission' in changes) {
            this.refreshDiscussions();
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.parentWorking$.complete();
    }

    /**
     * Uses the edit and approve permissions rather than `canEdit`, as messages
     * can be sent in every mission status except finalised.
     */
    private refreshCanSendMessage() {
        const canEditOrApprove =
            this.mission.availableActions?.hasEdit === true ||
            this.approval?.availableActions?.hasApprove === true ||
            findActiveApprovals(this.mission.approvals).some(
                approval => approval.availableActions?.hasApprove === true
            );
        if (this.mission.status === DisplayableMissionDto.Status.FINALISED) {
            this.canSendMessage = false;
            this.readOnlyMessage =
                'This mission has been finalised. No new messages can be added.';
        } else if (!canEditOrApprove) {
            this.canSendMessage = false;
            this.readOnlyMessage =
                "You can read this mission's messages. Only people who can edit or approve the mission can send one.";
        } else {
            this.canSendMessage = true;
            this.readOnlyMessage = null;
        }
    }

    getName(discussion: MissionMessageDto) {
        return `${discussion.sender.firstName} ${discussion.sender.lastName}`;
    }

    refreshDiscussions() {
        if (this.mission == null) {
            return;
        }
        // FIXME: Verify that mission.organisationId is the correct managing organisation ID
        const managingOrganisationId =
            this.mission.organisationId ?? this.organisation?.id;
        if (managingOrganisationId == null) {
            this.logging.error(
                null,
                'Cannot load discussions: missing managing organisation ID'
            );
            return;
        }
        this.missionService
            .findDiscussions(this.mission.id, managingOrganisationId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: discussions => {
                    const reversedDiscussion = [...discussions];
                    reversedDiscussion.reverse();
                    this.discussions = reversedDiscussion;
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error loading messages: ${error.message}`
                    );
                }
            })
            .add(this.loadingTracker.createTracker());
    }

    sendMessage() {
        if (!this.canSendMessage || this.isWorking) {
            return;
        }
        this.missionService
            .createDiscussion(this.mission.id, this.message)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: () => {
                    this.message = '';
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error sending message: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }
}
